import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { RootState, AppDispatch } from "../../store";
import { fetchList, resetState, setCursorHistory } from "../../store/slices/listSlice";
import EmptyListingScreen from "../emptyScreens/EmptyListingScreen";
import Paginate from "../tables/Paginate";
import useTranslation from "../CustomHooks/useTranslation";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import GridTableTopBar from "./GridTableTopBar";
import GridTableHeader from "./GridTableHeader";
import { DynamicFilterConfig } from "../DynamicComponent/DynamicFilterPanel";
import AccessDeniedScreen from "../emptyScreens/AccessDenied";

interface MainTableProps {
  staticText: {
    emptyScreenText: { heading1: string; heading2: string };
  };
  noUserSelectedText?: { heading1: string; heading2: string };
  mainEndPoint: string;
  TableDetails: any;
  handleActions?: (id: string, key: string, text?: string, row?: any) => void;
  params?: { [key: string]: string };
  extraExportParams?: { [key: string]: string };
  searchPlaceholder?: string;
  filters: boolean;
  extraParams?: string;
  exporting: boolean;
  serviceNow?: boolean;
  displayTotalRecords?: boolean;
  noListing?: boolean;
  notfoundRoute?: { [key: string]: { [key: string]: string } };
  title?: string;
  placeholderClassName?: string;
  dynamicFilterConfig?: DynamicFilterConfig;
  // Lets a page-level filter panel (one rendered outside this component, e.g. the
  // Account Statement panel in renderExtraHeadings) apply filters through the very
  // same handler the built-in top-bar filter uses. Applying filters by writing the
  // URL directly instead skips the cursor/page reset that handleFilter performs,
  // which leaves a stale keyset cursor to be sent with the new filter set.
  onFilterRef?: React.MutableRefObject<((filters: { [key: string]: unknown } | null) => void) | null>;
  renderTopBarExtras?: (args: {
    entireResponse: any;
    searchParams: URLSearchParams;
    setSearchParams: any;
    loading: boolean;
  }) => React.ReactNode;
  renderMiddleContent?: (args: {
    searchParams: URLSearchParams;
    loading: boolean;
  }) => React.ReactNode;
  renderSummary?: (args: { entireResponse: any; loading: boolean }) => React.ReactNode;
  renderExtraHeading?: (args: { entireResponse: any; loading: boolean }) => React.ReactNode;
  stickyHeader?: boolean;
  heightClassName?: string;
  hasListPrivilege?: boolean;
  hasActivateDeactivateMDPrivilege?: boolean;
  hasViewMDPrivilege?: boolean;
  hasEditMDPrivilege?: boolean;
  addMorePadding?: boolean;
  customHeight?: string;
  actions?: string[];
  fetchListTrigger?: boolean;
  filterShimmer?: boolean;
  balanceSheet?: boolean;
  staticRowData?: { show: boolean; data: Record<string, any>; position?: "top" | "bottom" };
  bottomStaticRowData?: { show: boolean; data: Record<string, any>; position?: "top" | "bottom" };
  noSearch?: boolean;
  selectedUser?: {
    firstName: string;
    lastName: string;
    userCode: string;
    account_id: string;
  } | null;
  requireUserSelection?: boolean;
  // When true, no list request is made until a transaction date range is present in the URL.
  // Used for reports whose backend requires a date range (e.g. Geographical Sales Report).
  requireDateRange?: boolean;
  // Export is blocked (with a toast) until a transaction date range is applied; the listing itself still loads
  requireDateRangeForExport?: boolean;
  useAccountIdAsPath?: boolean;
  excludeSearchParam?: boolean;
  reportType?: string;
  ticketList?: boolean;
  staticData?: any[];
  hideTopBar?: boolean;
  paginateMarginTop?: number;
  mainTableHeight?: string;
  topMargin?: string;
  activeTab?: string;
  mainDivScroll?: boolean;
  listSubHeading?: boolean;
  forceFullWidth?: boolean;
  enableCheckboxes?: boolean;
  customEmptyScreenHeight?: string;
  emptyScreenHeight?: string;
  onSelectionChange?: (selectedIds: string[], allSelected: boolean) => void;
  listTitle?: string;
  renderTopRightContent?: (args: {
    searchParams: URLSearchParams;
    loading: boolean;
  }) => React.ReactNode;
}

export default function GridTable({
  staticText,
  mainEndPoint,
  TableDetails,
  handleActions,
  noListing,
  extraParams,
  params = {},
  extraExportParams = {},
  searchPlaceholder,
  filters,
  serviceNow,
  exporting,
  displayTotalRecords = false,
  notfoundRoute = {},
  title,
  placeholderClassName,
  dynamicFilterConfig,
  onFilterRef,
  renderTopBarExtras,
  renderMiddleContent,
  renderSummary,
  renderExtraHeading,
  stickyHeader,
  heightClassName,
  hasActivateDeactivateMDPrivilege,
  hasViewMDPrivilege,
  hasEditMDPrivilege,
  addMorePadding,
  customHeight,
  actions = [],
  fetchListTrigger,
  filterShimmer = false,
  balanceSheet = false,
  hasListPrivilege = true,
  staticRowData,
  bottomStaticRowData,
  noSearch,
  selectedUser = null,
  requireUserSelection = false,
  requireDateRange = false,
  requireDateRangeForExport = false,
  useAccountIdAsPath = false,
  excludeSearchParam = false,
  noUserSelectedText,
  reportType,
  ticketList,
  staticData,
  hideTopBar = false,
  paginateMarginTop,
  mainTableHeight,
  topMargin,
  activeTab,
  mainDivScroll = false,
  listSubHeading,
  forceFullWidth = false,
  enableCheckboxes = false,
  customEmptyScreenHeight,
  emptyScreenHeight,
  onSelectionChange,
  listTitle,
  renderTopRightContent
}: MainTableProps) {
  const dispatch = useDispatch<AppDispatch>();
  const listState = useSelector((state: RootState) => state.list);
  const { loading, error, data, recordCount, entireResponse, nextCursor, hasMore, lastFulfilledPageIndex, cursorHistory: cursorHistoryFromRedux } = listState;
  const cursorHistory = cursorHistoryFromRedux ?? [null];
  const { translate } = useTranslation(breadcrumbsContent);
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams(params);

  // Checkbox state management
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [allPagesSelected, setAllPagesSelected] = useState<boolean>(false);
  const [currentPageSelected, setCurrentPageSelected] = useState<boolean>(false);

  // Track previous query params for selection reset
  const [prevQueryParams, setPrevQueryParams] = useState<{page_number?: string, user_role?: string, search?: string}>({});

  // Cursor-based pagination for Recharge Report and Account Statement Report (stock & money)
const CURSOR_PAGINATION_REPORTS = [
    "Recharge Report",
    "Account Statement Report",
    "Unsuccessful Transaction Report",
    "Merchant / Customer eTop Up Wallet Balance Report",
    "MNO Wise Sales Trend Report",
    "Fund Transfer Logs",
    "User Activity Logs",
    "Stock Transfer Report",
    "Geographical Sales Report",
    "ZoodPay Profitability Report",
  ];
  const useCursorPagination = CURSOR_PAGINATION_REPORTS.includes(reportType as string);
  const CURSOR_PAGE_LIMIT = 10;
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  // Ref so we always use the cursor from the latest API response when building the next request (avoids race with setState)

  // Pagination logic for static data
  const currentPage = Number(searchParams.get("page_number")) || 1;
  const itemsPerPage = 10;
  const paginatedStaticData = staticData ? staticData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : [];
  const staticRecordCount = staticData ? staticData.length : 0;

  // Request ID tracking to prevent stale API responses from overwriting current data
  const requestIdRef = useRef<string>('');
  const generateRequestId = () => `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestedPage1NoCursorRef = useRef(false);
  // Prevents double API call when useEffect at line ~211 syncs params to URL on mount
  const skipNextFetchRef = useRef(false);

  // Initialize searchParams from props if empty (only on mount)
  useEffect(() => {
    if ([...searchParams?.keys()].length === 0 && Object.keys(params).length) {
      skipNextFetchRef.current = true;
      setSearchParams(new URLSearchParams(params), { replace: true });
    }
  }, []);

  const filersWithEmpty = useMemo(
    () => ({
      heading1: translate("No results found"),
      heading2: translate("Try adjusting your filter's or search criteria"),
      img: "search_empty_screen_img",
    }),
    [translate]
  );

  useEffect(() => {
    const currentParams = Object.fromEntries(searchParams.entries());
    const newParams = { ...params };

    // Check if current params differ from expected params
    const dynamicParams = dynamicFilterConfig ? Object.values(dynamicFilterConfig).flatMap(conf =>
      typeof conf.searchKey === 'string' ? [conf.searchKey] : [conf.searchKey.start, conf.searchKey.end]
    ) : [];
    const allowedParams = ['page_number', 'search', 'sort', 'transaction_view', 'log_type', ...(useCursorPagination ? ['cursor'] : []), ...dynamicParams];
    const paramsDiffer = Object.keys(currentParams).some(key =>
      !newParams.hasOwnProperty(key) && !allowedParams.includes(key)
    ) || Object.keys(newParams).some(key =>
      currentParams[key] !== newParams[key]
    );

    if (paramsDiffer) {
      skipNextFetchRef.current = true;
      // For Account Statement Report, ensure we only keep allowed parameters
      if (reportType === "Account Statement Report") {
        const allowedParams = new URLSearchParams();

        // Only add params that are explicitly provided
        Object.entries(newParams).forEach(([key, value]) => {
          allowedParams.set(key, value);
        });

        // Preserve date filters if they exist in current URL and user is selected
        const currentUrlParams = new URLSearchParams(window.location.search);
        if (selectedUser && currentUrlParams.has('transaction_start_date')) {
          allowedParams.set('transaction_start_date', currentUrlParams.get('transaction_start_date')!);
        }
        if (selectedUser && currentUrlParams.has('transaction_end_date')) {
          allowedParams.set('transaction_end_date', currentUrlParams.get('transaction_end_date')!);
        }

        setSearchParams(allowedParams, { replace: true });
      } else {
        // For other reports, use the provided params
        setSearchParams(new URLSearchParams(newParams), { replace: true });
      }
    }
  }, [reportType, selectedUser]);
  const getList = () => {
    // Don't fetch if user selection is required but no user is selected
    if (requireUserSelection && !selectedUser) {
      return;
    }

    // Don't fetch if a date range is required but not yet selected (backend rejects
    // dateless requests). The user picks a range via the filter panel first.
    if (
      requireDateRange &&
      (!searchParams.get("transaction_start_date") || !searchParams.get("transaction_end_date"))
    ) {
      return;
    }

    // Start with the params prop (which contains the correct sort key for the current report type)
    const apiParams = { ...params };

    // Get raw sort parameter from URL to preserve + sign
    const urlSearch = window.location.search;
    const sortMatch = urlSearch.match(/[?&]sort=([^&]+)/);
    const rawSort = sortMatch ? decodeURIComponent(sortMatch[1]) : null;

    // Add search parameters from URL, allowing URL sort to override params sort
    const searchParamsObj = Object.fromEntries(searchParams.entries());
    Object.entries(searchParamsObj).forEach(([key, value]) => {
      // Only add log_type if it's in the base params
      if (key === 'log_type' && !params.hasOwnProperty('log_type')) {
        return;
      }
      // Skip sort parameter, we'll use rawSort instead
      if (key === 'sort') {
        return;
      }
      // For cursor pagination, do not add page_number or cursor from URL (we use limit + cursor from state only)
      if (useCursorPagination && (key === 'page_number' || key === 'cursor')) {
        return;
      }
      apiParams[key] = value;
    });

    // URL = source of truth for cursor: read from URL so refresh keeps same page.
    if (useCursorPagination) {
      delete apiParams.page_number;
      if (reportType !== "Fund Transfer Logs" && reportType !== "User Activity Logs" && reportType !== "Stock Transfer Report") {
        apiParams.limit = String(CURSOR_PAGE_LIMIT);
      }
      const urlCursor = searchParams.get("cursor");
      if (urlCursor) {
        apiParams.cursor = urlCursor;
      } else {
        delete apiParams.cursor;
      }
    }

    // Add raw sort parameter if it exists
    if (rawSort) {
      apiParams.sort = rawSort;
    }

    // Build the endpoint URL
    let endpoint = mainEndPoint;

    // Handle account_id as path parameter or query parameter
    if (selectedUser?.account_id) {
      if (useAccountIdAsPath) {
        // Add account_id as path parameter
        endpoint = `${mainEndPoint}/${selectedUser.account_id}/statement`;
      } else {
        // Add account_id as query parameter
        apiParams.account_id = selectedUser.account_id;
      }
    }

    // Add user search term (firstName + lastName) to params if user is selected and not excluded
    if (selectedUser?.firstName && selectedUser?.lastName && !excludeSearchParam) {
      const userSearchTerm = `${selectedUser.firstName} ${selectedUser.lastName}`;
      apiParams.search = userSearchTerm;
    }

    // For Account Statement Report, preserve the search parameter if it exists in URL
    if (reportType === "Account Statement Report" && searchParams.get("search")) {
      apiParams.search = searchParams.get("search")!;
    }

    // Remove sort parameter for Account Statement Report
    if (reportType === "Account Statement Report") {
      delete apiParams.sort;
    }

    // Remove sort parameter for On Target Achievement of MNO Report
    if (reportType === "On Target Achievement of MNO Report" || reportType === "Reversal Report") {
      delete apiParams.sort;
    }

    // Remove sort parameter for CVM Report
    if (reportType === "CVM Report") {
      delete apiParams.sort;
    }

    // Remove sort parameter for MNO Wise Sales Trend Report (API does not support sort)
    if (reportType === "MNO Wise Sales Trend Report") {
      delete apiParams.sort;
    }

    // Remove sort parameter for ZoodPay Profitability Report
    if (reportType === "ZoodPay Profitability Report") {
      delete apiParams.sort;
    }

    // Add default sort if no sort parameter exists (but not for reports that don't support sorting)
    if (!apiParams.sort && TableDetails && reportType !== "On Target Achievement of MNO Report" && reportType !== "ZoodPay Profitability Report" && reportType !== "Merchant / Customer eTop Up Wallet Balance Report" && reportType !== "Geographical Sales Report" && reportType !== "CVM Report" && reportType !== "MNO Wise Sales Trend Report") {
      const sortableColumn = Object.entries(TableDetails).find(([, details]) => details.sort);
      if (sortableColumn) {
        const [, details] = sortableColumn;
        const sortKey = details.sortKey || details.id;
        apiParams.sort = `${sortKey}+desc`;
      }
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Extract sort parameter to handle + sign properly
    const sortParam = apiParams.sort;
    const paramsWithoutSort = { ...apiParams };
    delete paramsWithoutSort.sort;

    // Build query string
    let queryString = new URLSearchParams(paramsWithoutSort).toString();
    if (sortParam) {
      // URL encode the sort parameter to preserve + sign
      queryString += `${queryString ? '&' : ''}sort=${encodeURIComponent(sortParam)}`;
    }

    // Generate a unique request ID to track this specific request
    // This prevents stale responses from overwriting data when navigating between tabs
    const requestId = generateRequestId();
    requestIdRef.current = requestId;

    const urlPageNum = useCursorPagination
      ? Math.max(1, parseInt(searchParams.get("page_number") || "1", 10) || 1)
      : undefined;
    dispatch(
      fetchList({
        url: `${endpoint}?${queryString}&client_timezone=${timezone}${extraParams ? extraParams : ""}`,
        requestId,
        ...(useCursorPagination && urlPageNum != null ? { pageIndex: urlPageNum - 1 } : {}),
      })
    );
  };

  // URL = source of truth. On load/refresh: set currentPageIndex from URL only (no Redux overwrite).
  // When filters/sort/search change (excluding cursor and page_number), reset to first page.
  const prevSearchParamsRef = useRef<string>("");
  // Set when a filter/sort/search change has reset us to page 1, cleared once the URL
  // reflects that. While set, the Redux→URL sync effect must not write a page position
  // derived from the previous filter set's cursorHistory.
  const filterResetPendingRef = useRef(false);
  useEffect(() => {
    if (!useCursorPagination) return;
    const key = Array.from(searchParams.entries())
      .filter(([k]) => k !== "cursor" && k !== "page_number")
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join("&");
    const urlPage = searchParams.get("page_number");
    if (prevSearchParamsRef.current === "") {
      prevSearchParamsRef.current = key;
      const pageNum = Math.max(1, parseInt(urlPage || "1", 10) || 1);
      setCurrentPageIndex(pageNum - 1);
    } else if (prevSearchParamsRef.current !== key) {
      prevSearchParamsRef.current = key;
      requestedPage1NoCursorRef.current = false;
      dispatch(setCursorHistory([null]));
      setCurrentPageIndex(0);
      // Mark this filter set as reset-to-page-1 so the Redux→URL sync effect below
      // cannot write the pre-filter page position back into the URL. That effect runs
      // in the same commit as this one, while currentPageIndex still holds the old
      // page, so without this guard it re-adds page_number=N + the old cursor.
      filterResetPendingRef.current = true;
      // Normalize the URL to page 1 unconditionally, not just when a cursor is present.
      // The URL is the source of truth for the outgoing request (see the apiParams block
      // above). Callers that change filters outside handleFilter — e.g. the page-level
      // onFilter in Reports.tsx (Account Statement) — already delete the cursor but leave
      // their own page_number write in place; bailing out here on "no cursor in URL" left
      // page_number=N to be re-paired with a stale cursor by the sync effect.
      if (searchParams.get("cursor") || (urlPage && urlPage !== "1")) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("cursor");
          next.set("page_number", "1");
          return next;
        }, { replace: true });
      }
    }
  }, [searchParams, useCursorPagination, dispatch, setSearchParams]);

  // Sync Redux → URL when we have the cursor in Redux. When URL is source of truth (e.g. refresh)
  // do not overwrite URL until we've applied it (avoid race: sync runs before restore with currentPageIndex still 0).
  useEffect(() => {
    if (!useCursorPagination) return;
    const urlCursor = searchParams.get("cursor");
    const urlPage = searchParams.get("page_number");
    // A filter change just invalidated the current page position. Stay out of the way
    // until the URL settles on page 1 with no cursor; writing here would restore the
    // pre-filter page/cursor pair and send the old keyset predicate with the new range.
    if (filterResetPendingRef.current) {
      if ((urlPage == null || urlPage === "1") && urlCursor == null) {
        filterResetPendingRef.current = false;
      }
      return;
    }
    const desiredPage = String(currentPageIndex + 1);
    const desiredCursor = currentPageIndex === 0 ? null : (cursorHistory[currentPageIndex] ?? null);
    if (desiredPage === urlPage && desiredCursor === urlCursor) return;
    if (currentPageIndex > 0 && desiredCursor == null && urlCursor != null) return;
    if (urlCursor && urlPage && currentPageIndex === 0) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page_number", desiredPage);
      if (currentPageIndex === 0) next.delete("cursor");
      else if (desiredCursor != null) next.set("cursor", desiredCursor);
      return next;
    });
  }, [useCursorPagination, currentPageIndex, searchParams, cursorHistory, setSearchParams]);

  useEffect(() => {
    if (staticData) return;

    // Skip this fetch if params are being synced to the URL (prevents double API call on mount)
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    // Clear any pending debounced fetch
    if (fetchDebounceRef.current) {
      clearTimeout(fetchDebounceRef.current);
    }

    // Debounce the API call to prevent multiple calls in quick succession
    // This handles cases where searchParams changes multiple times during initialization
    fetchDebounceRef.current = setTimeout(() => {
      if (
        requireDateRange &&
        (!searchParams.get("transaction_start_date") || !searchParams.get("transaction_end_date"))
      ) {
        dispatch(resetState());
        return;
      }
      getList();
    }, 0);
    // mainEndPoint is included so switching directly between two reports that
    // reuse this component (e.g. Upfront Sales -> Upfront Purchase) refetches
    // even when the search params are identical/empty.
  }, [searchParams, fetchListTrigger, selectedUser?.account_id, mainEndPoint]);

  // Reset pagination when the selected user changes - a page/cursor position
  // from the previously selected user's result set is meaningless for the new one.
  // Writes the URL directly (like handleFilter/handleSort do) rather than relying
  // on the "sync Redux -> URL" effect below, which intentionally no-ops when
  // currentPageIndex is 0 but the URL still has a stale cursor+page_number.
  const prevSelectedUserAccountIdRef = useRef<string | undefined>(selectedUser?.account_id);
  useEffect(() => {
    if (!useCursorPagination) return;
    if (prevSelectedUserAccountIdRef.current === selectedUser?.account_id) return;
    prevSelectedUserAccountIdRef.current = selectedUser?.account_id;
    requestedPage1NoCursorRef.current = false;
    dispatch(setCursorHistory([null]));
    setCurrentPageIndex(0);
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      updated.set("page_number", "1");
      updated.delete("cursor");
      return updated;
    });
  }, [selectedUser?.account_id, useCursorPagination, dispatch, setSearchParams]);

  // After refresh on page N we only have cursor for page N; going Previous we request page 1 (no cursor) → sync to page 1.
  useEffect(() => {
    if (!useCursorPagination) return;
    if (lastFulfilledPageIndex !== 0 || currentPageIndex <= 0) return;
    if (!requestedPage1NoCursorRef.current) return;
    requestedPage1NoCursorRef.current = false;
    setCurrentPageIndex(0);
  }, [useCursorPagination, lastFulfilledPageIndex, currentPageIndex]);

  const handleSearch = (searchTerm: string) => {
    handleFilter({ search: searchTerm });
  };

  const handleFilter = useCallback(
    (filters: { [key: string]: any } | null) => {
      if (useCursorPagination) {
        dispatch(setCursorHistory([null]));
        setCurrentPageIndex(0);
      }
      setSearchParams((prev) => {
        const updatedParams = new URLSearchParams(prev);

        updatedParams.set("page_number", "1");

        if (useCursorPagination) {
          updatedParams.delete("cursor");
        }

        if (filters === null && dynamicFilterConfig) {
          Object.values(dynamicFilterConfig).forEach((conf) => {
            if (conf.type === "date" && typeof conf.searchKey === "object") {
              updatedParams.delete(conf.searchKey.start);
              updatedParams.delete(conf.searchKey.end);
            } else if (typeof conf.searchKey === "string") {
              updatedParams.delete(conf.searchKey);
            }
          });

          updatedParams.delete("region");
          updatedParams.delete("province");
          updatedParams.delete("district");
          updatedParams.delete("country");
        } else if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (
              value &&
              typeof value === "object" &&
              typeof (value as any).format === "function"
            ) {
              updatedParams.set(key, (value as any).format("YYYY-MM-DD"));
            } else if (value !== null && value !== undefined && value !== "") {

              updatedParams.set(key, String(value));
            } else {

              updatedParams.delete(key);
            }
          });
        }
        return updatedParams;
      });
    },
    [dynamicFilterConfig, setSearchParams, useCursorPagination, dispatch]
  );

  // Expose handleFilter so a filter panel rendered outside this component applies
  // filters through the same path as the built-in one, cursor/page reset included.
  useEffect(() => {
    if (!onFilterRef) return;
    onFilterRef.current = handleFilter;
    return () => {
      onFilterRef.current = null;
    };
  }, [onFilterRef, handleFilter]);

  const handleSort = useCallback(
    (key: string) => {
      if (useCursorPagination) {
        dispatch(setCursorHistory([null]));
        setCurrentPageIndex(0);
      }
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev);
        const currentSort = updated.get("sort");
        let newSort;
        if (currentSort) {
          const sortArray = currentSort.split(",");
          const existingSort = sortArray.find((s) => s.startsWith(key));
          if (existingSort) {
            const [field, order] = existingSort.split("+");
            const newOrder = order === "asc" ? "desc" : "asc";
            newSort = [`${field}+${newOrder}`, ...sortArray.filter((s) => !s.startsWith(key))].join(",");
          } else {
            newSort = [`${key}+asc`, ...sortArray].join(",");
          }
        } else {
          newSort = `${key}+asc`;
        }
        updated.set("page_number", "1");
        updated.set("sort", newSort);
        if (useCursorPagination) {
          updated.delete("cursor");
        }
        return updated;
      });
    },
    [setSearchParams, useCursorPagination, dispatch]
  );

  const navigator = useCallback(() => {
    dispatch(resetState());
    navigate("/not-found", notfoundRoute);
  }, [dispatch, navigate, notfoundRoute]);

  useEffect(() => {
    if (error !== null) {
      navigator();
      dispatch(resetState());
    }
  }, [error, navigator, dispatch]);

  // Checkbox handlers
  const handleSelectAll = useCallback(() => {
    if (allPagesSelected) {
      // If all pages are selected, deselect everything
      setSelectedIds([]);
      setAllPagesSelected(false);
      setCurrentPageSelected(false);
      onSelectionChange?.([], false);
    } else if (currentPageSelected) {
      // If current page is selected, deselect current page
      setSelectedIds([]);
      setCurrentPageSelected(false);
      setAllPagesSelected(false);
      onSelectionChange?.([], false);
    } else {
      // Select current page only
      const currentPageData = staticData ? paginatedStaticData : data;
      if (currentPageData && currentPageData.length > 0) {
        const pageIds = currentPageData.map((item: any) => item.id || item.user_code || item.user_id).filter(Boolean);
        setSelectedIds(pageIds);
        setCurrentPageSelected(true);
        onSelectionChange?.(pageIds, false);
      }
    }
  }, [allPagesSelected, currentPageSelected, staticData, paginatedStaticData, data, onSelectionChange]);

  const handleSelectAllPages = useCallback(() => {
    if (allPagesSelected) {
      // If all pages are already selected, deselect everything
      setAllPagesSelected(false);
      setCurrentPageSelected(false);
      setSelectedIds([]);
      onSelectionChange?.([], false);
    } else {
      // Select all pages
      setAllPagesSelected(true);
      setCurrentPageSelected(false);
      setSelectedIds([]);
      onSelectionChange?.([], true);
    }
  }, [allPagesSelected, onSelectionChange]);

  const handleRowSelect = useCallback((id: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedIds((prev: string[]) => [...prev, id]);
      setCurrentPageSelected(false);
      setAllPagesSelected(false);
    } else {
      if (allPagesSelected) {
        // If all pages are selected and we're unchecking a row,
        // select all pages except this specific row
        const currentPageData = staticData ? paginatedStaticData : data;
        if (currentPageData && currentPageData.length > 0) {
          const pageIds = currentPageData.map((item: any) => item.id || item.user_code || item.user_id).filter(Boolean);
          const remainingIds = pageIds.filter((pageId: string) => pageId !== id);
          setSelectedIds(remainingIds);
          setAllPagesSelected(false);
          setCurrentPageSelected(false);
        }
      } else {
        setSelectedIds((prev: string[]) => prev.filter((selectedId: string) => selectedId !== id));
        setCurrentPageSelected(false);
        setAllPagesSelected(false);
      }
    }
  }, [allPagesSelected, staticData, paginatedStaticData, data]);

  // Reset selection when specific query params change
  useEffect(() => {
    const currentPageNumber = searchParams.get('page_number');
    const currentUserRole = searchParams.get('user_role');
    const currentSearch = searchParams.get('search');

    const currentParams = {
      page_number: currentPageNumber || undefined,
      user_role: currentUserRole || undefined,
      search: currentSearch || undefined
    };

    // Check if any of the tracked params have changed
    const hasParamsChanged =
      prevQueryParams.page_number !== currentParams.page_number ||
      prevQueryParams.user_role !== currentParams.user_role ||
      prevQueryParams.search !== currentParams.search;

    if (hasParamsChanged && Object.keys(prevQueryParams).length > 0) {
      // Reset all selections
      setSelectedIds([]);
      setAllPagesSelected(false);
      setCurrentPageSelected(false);
      onSelectionChange?.([], false);
    }

    // Update previous params
    setPrevQueryParams(currentParams);
  }, [onSelectionChange]);

  // Reset selection when page changes (keep existing logic for other cases)
  useEffect(() => {
    if (allPagesSelected) {
      // Keep all pages selected when navigating
      return;
    }
    setCurrentPageSelected(false);
    setSelectedIds([]);
  }, [currentPage, allPagesSelected]);

  // Update selection change callback
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedIds, allPagesSelected);
    }
  }, [selectedIds, allPagesSelected, onSelectionChange]);

  // Auto-check Select All when manually selected rows equal current page rows
  useEffect(() => {
    if (!allPagesSelected && !currentPageSelected && selectedIds.length > 0) {
      const currentPageData = staticData ? paginatedStaticData : data;
      if (currentPageData && currentPageData.length > 0) {
        const currentPageIds = currentPageData.map((item: any) => item.id || item.user_code || item.user_id).filter(Boolean);
        const isCurrentPageFullySelected = currentPageIds.every((id: string) => selectedIds.includes(id));

        if (isCurrentPageFullySelected && selectedIds.length === currentPageIds.length) {
          setCurrentPageSelected(true);
        }
      }
    }
  }, [selectedIds, allPagesSelected, currentPageSelected, staticData, paginatedStaticData, data]);

  const hasActiveFilters = useMemo(
    () => {
      const standardParams = ['page_number', 'sort'];
      return Array.from(searchParams.entries()).some(([key]) =>
        !standardParams.includes(key)
      );
    },
    [searchParams]
  );

  const buildExportEndpoint = useCallback(() => {
    let exportEndpoint = mainEndPoint;
    if (selectedUser?.account_id && useAccountIdAsPath) {
      exportEndpoint = `${mainEndPoint}/${selectedUser.account_id}`;
    }
    if (reportType === "Account Statement Report") {
      exportEndpoint = `${mainEndPoint}/${selectedUser?.account_id}/statement`
    }
    const exportParams = new URLSearchParams([
      ...Array.from(searchParams.entries()).filter(([key]) => key !== "page_number" && key !== "cursor"),
      ...(extraExportParams ? Object.entries(extraExportParams) : []),
    ]);
    if (reportType === "Account Statement Report" || reportType === "CVM Report" || reportType === "MNO Wise Sales Trend Report") {
      exportParams.delete('sort');
    }
    if (selectedUser?.account_id && !useAccountIdAsPath) {
      exportParams.set("account_id", selectedUser.account_id);
    }
    // Include mno_ids parameter for MNO Wise eTop Up Purchase Report export
    if (reportType === "MNO Wise eTop Up Purchase Report" && searchParams.get('mno_ids')) {
      exportParams.set('mno_ids', searchParams.get('mno_ids')!);
    }
    // Include mno_ids parameter for Upfront Purchase Report export
    if (reportType === "Upfront Purchase Report" && searchParams.get('mno_ids')) {
      exportParams.set('mno_ids', searchParams.get('mno_ids')!);
    }
    return `${exportEndpoint}/export?${exportParams.toString()}`;
  }, [mainEndPoint, selectedUser, useAccountIdAsPath, searchParams, extraExportParams, reportType]);

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page_number: String(page),
      });
    },
    [searchParams, setSearchParams]
  );

  const handleCursorPrev = useCallback(() => {
    const prevIndex = Math.max(0, currentPageIndex - 1);
    const prevCursor = prevIndex === 0 ? null : (cursorHistory[prevIndex] ?? null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page_number", String(prevIndex + 1));
      if (prevCursor == null) next.delete("cursor");
      else next.set("cursor", prevCursor);
      return next;
    });
    setCurrentPageIndex(prevIndex);
  }, [currentPageIndex, cursorHistory, setSearchParams]);

  const handleCursorNext = useCallback(() => {
    if (!hasMore || !nextCursor) return;
    const nextIndex = currentPageIndex + 1;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page_number", String(nextIndex + 1));
      next.set("cursor", nextCursor);
      return next;
    });
    setCurrentPageIndex(nextIndex);
  }, [hasMore, nextCursor, currentPageIndex, setSearchParams]);

  if (requireUserSelection && !selectedUser) {
    const emptyScreenText = noUserSelectedText || {
      heading1: "Select a user to view data",
      heading2: "Please choose a user from the dropdown above",
    };

    return (
      <div className="mt-1.5 bg-white h-maintableheight rounded-lg">
        {renderExtraHeading && renderExtraHeading({ entireResponse, loading })}
        <div className="flex items-center justify-center h-accountStatementEmpty">
          <EmptyListingScreen emptyScreenText={emptyScreenText} imageHight="h-60" />
        </div>
      </div>
    );
  }

  // When a date range is required but not yet selected, no fetch has run (data is empty).
  // Fall through to the normal layout so the filter bar renders, and show a prompt in the
  // empty-listing area instead of the generic "no data" message.
  const dateRangeMissing =
    requireDateRange &&
    (!searchParams.get("transaction_start_date") || !searchParams.get("transaction_end_date"));

  // Redux `loading` starts true and only clears when a fetch resolves. When the fetch is
  // intentionally gated (no date range yet), force loading off so we show the prompt, not a
  // perpetual shimmer.
  const effectiveLoading = loading && !dateRangeMissing;

  return (
    <div className={`${topMargin || "mt-1.5"} bg-white rounded-lg ${mainDivScroll ? `min-h-[calc(100vh-223px)] max-h-[calc(100vh-223px)] overflow-y-auto ${stickyHeader ? "scrollHide" : "scrollBar"}` : mainTableHeight || "h-maintableheight"}`}>
      <div className={stickyHeader ? "sticky top-0 z-20 bg-white" : undefined}>
        {renderExtraHeading && (
          <div>
            {renderExtraHeading({ entireResponse, loading })}
          </div>
        )}
        {listSubHeading && <div className="mx-4 pt-4 mt-4 font-medium border-t border-gray-200">List of all advance payments</div>}
        {(!noListing && hasListPrivilege && !hideTopBar) && (
          <GridTableTopBar
            onSearch={handleSearch}
            onFilter={handleFilter}
            loading={loading}
            searchValue={searchParams.get("search") || ""}
            searchParams={searchParams}
            searchPlaceholder={searchPlaceholder}
            filters={filters}
            serviceNow={serviceNow}
            exporting={exporting}
            endpoint={buildExportEndpoint()}
            disableExport={data && data.length === 0}
            requireDateRangeForExport={requireDateRangeForExport}
            disableSearchAndFilter={!dateRangeMissing && recordCount === 0 && !hasActiveFilters && searchParams.get("search") === null}
            placeholderClassName={placeholderClassName}
            dynamicFilterConfig={dynamicFilterConfig}
            recordCount={recordCount}
            customHeight={customHeight}
            filterShimmer={filterShimmer}
            balanceSheet={balanceSheet}
            noSearch={noSearch}
            reportType={reportType}
            ticketList={ticketList}
            accountSummary={activeTab === 'money' ? entireResponse : null}
            enableCheckboxes={enableCheckboxes}
            onSelectAll={handleSelectAll}
            onSelectAllPages={handleSelectAllPages}
            currentPageSelected={currentPageSelected}
            allPagesSelected={allPagesSelected}
            selectedCount={selectedIds.length}
            totalCount={staticData ? staticRecordCount : recordCount}
            currentPageData={staticData ? paginatedStaticData : data}
            selectedIds={selectedIds}
            middleContent={renderMiddleContent && renderMiddleContent({ searchParams, loading })}
            listTitle={listTitle}
            topRightContent={renderTopRightContent && renderTopRightContent({ searchParams, loading })}
          />
        )}

        {renderTopBarExtras && renderTopBarExtras({ entireResponse, searchParams, setSearchParams, loading })}
      </div>
      <div>
        {effectiveLoading && !staticData ? (
          <div className="p-4">
            <GridTableHeader
              listData={data}
              TableDetails={TableDetails}
              handleActions={handleActions || (() => {})}
              loading={loading}
              handleSort={handleSort}
              heightClassName={mainDivScroll ? "h-auto" : heightClassName}
              staticRowData={staticRowData}
              bottomStaticRowData={bottomStaticRowData}
              totalRows="0"
              forceFullWidth={forceFullWidth}
            />
            {renderSummary && renderSummary({ entireResponse, loading })}
          </div>
        ) : !hasListPrivilege ? (
          <AccessDeniedScreen />
        ) : error ? (
          <div className="text-center py-8 text-red-500">error</div>
        ) : !noListing && (staticData ? paginatedStaticData.length > 0 : data && data.length > 0) ? (
          <div className="p-4">
            <GridTableHeader
              listData={staticData ? paginatedStaticData : data}
              TableDetails={TableDetails}
              handleActions={handleActions || (() => {})}
              loading={staticData ? false : loading}
              handleSort={handleSort}
              searchParams={searchParams}
              getList={getList}
              heightClassName={mainDivScroll ? "h-auto" : heightClassName}
              hasActivateDeactivateMDPrivilege={hasActivateDeactivateMDPrivilege}
              hasViewMDPrivilege={hasViewMDPrivilege}
              hasEditMDPrivilege={hasEditMDPrivilege}
              staticRowData={staticRowData}
              bottomStaticRowData={bottomStaticRowData}
              totalRows={String(staticData ? staticRecordCount : recordCount || 0)}
              forceFullWidth={forceFullWidth}
              enableCheckboxes={enableCheckboxes}
              selectedIds={selectedIds}
              onRowSelect={handleRowSelect}
              allPagesSelected={allPagesSelected}
            />
            {renderSummary && renderSummary({ entireResponse, loading })}
            {useCursorPagination ? (
              (data.length > 0 || (recordCount != null && Number(recordCount) > 0)) && (
                <div className="flex items-center w-full z-[9] cursor-default relative mt-6" data-testid="cursor-paginator">
                  <div className="flex items-center w-full justify-between cursor-default" data-testid="paginator">
                    <button
                      type="button"
                      onClick={handleCursorPrev}
                      disabled={loading || currentPageIndex === 0}
                      className="rounded-lg px-4 py-2.5 flex items-center justify-center gap-1.5 text-sm font-medium min-w-[110px] transition bg-transparent border border-transparent disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                      style={loading || currentPageIndex === 0 ? { backgroundColor: '#E7EDF5', color: '#144EA2', border: '1px solid #144EA2' } : { backgroundColor: '#E7EDF5', color: '#144EA2', border: '1px solid #144EA2' }}
                      data-testid="previous"
                      aria-label="Previous page"
                    >
                      <span className="text-base font-medium">&lt;</span>
                      <span>Previous</span>
                    </button>
                    <span className="text-sm font-[500] text-[#1F1F1F] px-4 py-2 flex-1 flex justify-center">
                      Page {currentPageIndex + 1}
                      {(recordCount != null && Number(recordCount) >= 0) && ` · ${recordCount} total`}
                    </span>
                    <button
                      type="button"
                      onClick={handleCursorNext}
                      disabled={loading || !hasMore || !nextCursor}
                      className="rounded-lg px-4 py-2.5 flex items-center justify-center gap-1.5 text-sm font-medium min-w-[110px] transition bg-transparent border border-transparent disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                      style={loading || !hasMore || !nextCursor ? { backgroundColor: '#E7EDF5', color: '#144EA2', border: '1px solid #144EA2' } : { backgroundColor: '#E7EDF5', color: '#144EA2', border: '1px solid #144EA2' }}
                      data-testid="next"
                      aria-label="Next page"
                    >
                      <span>Next</span>
                      <span className="text-base font-medium">&gt;</span>
                    </button>
                  </div>
                  {displayTotalRecords && title && (
                    <p className="absolute top-1/2 -translate-y-1/2 right-0 text-body2 text-primary_text">
                      {`Total Number of ${title} : `}<span className="text-button text-primary_text font-semibold">{recordCount}</span>
                    </p>
                  )}
                </div>
              )
            ) : ((staticData && staticRecordCount > 10) || (!staticData && recordCount && Number(recordCount) > 10)) && (
              <Paginate
                totalPages={Math.ceil((staticData ? staticRecordCount : Number(recordCount || 0)) / 10)}
                currentPage={Number(searchParams.get("page_number")) || 1}
                onPageChange={handlePageChange}
                loading={staticData ? false : loading}
                displayTotalRecords={displayTotalRecords}
                title={title}
                marginTop={String(paginateMarginTop || 0)}
              />
            )}
          </div>
        ) : !effectiveLoading && ((staticData && staticRecordCount === 0) || (!staticData && (!data || data.length === 0))) ? (
          <div
            className={`flex items-center justify-center ${ticketList
              ? "h-[calc(100vh-570px)]"
              : selectedUser && Number(recordCount) === 0
                ? "h-childernTabHight2"
                : selectedUser
                  ? "h-accountStatementEmpty" :
                  emptyScreenHeight ? emptyScreenHeight
                  : "h-emptyscreenheight"
              }`}
          >

            <EmptyListingScreen
              emptyScreenText={
                dateRangeMissing
                  ? {
                      heading1: "Select a date range to view data",
                      heading2: "Please choose a transaction date range from the filters above",
                    }
                  : hasActiveFilters ? filersWithEmpty : staticText.emptyScreenText
              }
              imageHight={
                ticketList ? "h-[200px] xs:h-[220px] sm:h-[240px] md:h-[260px] lg:h-[100px] xl:h-[280px] 2xl:h-[300px]" : customEmptyScreenHeight ? customEmptyScreenHeight : "h-60"
              }
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}